"use client"

import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { usePage } from "@inertiajs/react"
import { RefreshCw, Home, WifiOff, FileQuestion, AlertTriangle } from "lucide-react"

const errorConfig = {
  storage_unavailable: {
    Icon: WifiOff,
    defaultTitle: "Oops! Service Unavailable",
    defaultMessage: "Our file storage is taking a short break. This usually fixes itself — just give the page a refresh!",
  },
  not_found: {
    Icon: FileQuestion,
    defaultTitle: "Page Not Found",
    defaultMessage: "This page seems to have wandered off. Let's get you back to familiar ground.",
  },
  server_error: {
    Icon: AlertTriangle,
    defaultTitle: "Something Went Wrong",
    defaultMessage: "Something hiccupped on our end. A quick refresh often does the trick!",
  },
}

function LogoWithFallback({ src, alt, className }) {
  const [failed, setFailed] = useState(false)

  if (failed || !src) {
    return (
      <div className={`${className} bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md`}>
        <span>eK</span>
      </div>
    )
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`${className} rounded-full object-cover border-2 border-gray-100 shadow-md`}
      onError={() => setFailed(true)}
    />
  )
}

export default function SadPandaModal({ error, onDismiss }) {
  const { props } = usePage()
  const logoUrl = props.barangay?.bar_logo_url || props.bar_logo_url || null

  if (!error) return null

  const config = errorConfig[error.type] || errorConfig.server_error
  const title = error.title || config.defaultTitle
  const message = error.message || config.defaultMessage
  const { Icon } = config

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
        onClick={onDismiss}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 10 }}
          transition={{ type: "spring", damping: 22, stiffness: 280 }}
          className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl relative overflow-visible"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 sm:p-8 lg:p-10 relative z-10">
            <div className="flex items-center gap-3 mb-5">
              <LogoWithFallback
                src={logoUrl}
                alt="Barangay Logo"
                className="w-10 h-10 shrink-0"
              />
              <div>
                <div className="text-[#ffb800] font-bold text-2xl leading-none mb-0.5">{error.type === "not_found" ? 404 : error.type === "storage_unavailable" ? 503 : 500}</div>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-2">
              <Icon className="w-5 h-5 text-rose-400 shrink-0" />
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            </div>

            <p className="text-gray-500 text-sm mb-6 leading-relaxed">{message}</p>

            <div className="flex flex-col gap-2.5">
              {error.type !== "not_found" && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => window.location.reload()}
                  className="w-full bg-[#ffb800] text-[#1e1b4b] rounded-xl py-3 px-4 hover:bg-[#ffc933] transition-all duration-300 font-semibold shadow-lg flex items-center justify-center gap-2 text-sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh Page
                </motion.button>
              )}

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => { window.location.href = "/" }}
                className="w-full border-2 border-gray-200 text-gray-600 rounded-xl py-3 px-4 hover:bg-gray-50 transition-all duration-300 font-medium flex items-center justify-center gap-2 text-sm"
              >
                <Home className="w-4 h-4" />
                Go Home
              </motion.button>

              <button
                onClick={onDismiss}
                className="text-xs text-gray-400 hover:text-gray-500 transition-colors py-1 mt-0.5"
              >
                Close
              </button>
            </div>
          </div>

          <div className="hidden sm:block absolute -top-6 right-0 w-56 lg:w-64 h-72 lg:h-80 rotate-[3deg] overflow-hidden rounded-2xl shadow-2xl z-20">
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-transparent"></div>
            <img
              src="/images/cat.jpeg"
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none" }}
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
